// Journey progress persistence — the step, career path, policy-read state, and
// checklist state all used to live purely in React state, so any tab close,
// refresh, or accidental navigation lost all of it and dropped the employee
// back at the Profile step. This module survives all of that, keyed by email
// so a shared device doesn't bleed one employee's progress into another's.

const PREFIX = "kd_progress_";

export interface JourneyProgress {
  step: number;
  careerPath: string;
  readChapters: string[];
  checkedItems: string[];
  pendingItems: string[];
}

const EMPTY: JourneyProgress = { step: 1, careerPath: "", readChapters: [], checkedItems: [], pendingItems: [] };

/** The signed-in employee's email, set by lib/auth.ts once Microsoft Graph
 *  resolves the profile — available globally without prop-drilling. */
export function currentEmail(): string {
  if (typeof window === "undefined") return "";
  return sessionStorage.getItem("kd_email") || "";
}

function storageKey(email: string): string {
  return PREFIX + email.trim().toLowerCase();
}

export function loadProgress(email: string): JourneyProgress | null {
  if (!email || typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(storageKey(email));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<JourneyProgress>;
    return {
      step: typeof parsed.step === "number" && parsed.step >= 1 ? parsed.step : 1,
      careerPath: parsed.careerPath || "",
      readChapters: Array.isArray(parsed.readChapters) ? parsed.readChapters : [],
      checkedItems: Array.isArray(parsed.checkedItems) ? parsed.checkedItems : [],
      pendingItems: Array.isArray(parsed.pendingItems) ? parsed.pendingItems : [],
    };
  } catch {
    return null;
  }
}

export function saveProgress(email: string, patch: Partial<JourneyProgress>): void {
  if (!email || typeof window === "undefined") return;
  try {
    const current = loadProgress(email) || EMPTY;
    const next: JourneyProgress = { ...current, ...patch };
    localStorage.setItem(storageKey(email), JSON.stringify(next));
  } catch {
    /* storage full or unavailable — resuming just won't work, non-fatal */
  }
}

export function clearProgress(email: string): void {
  if (!email || typeof window === "undefined") return;
  try {
    localStorage.removeItem(storageKey(email));
  } catch {
    /* ignore */
  }
}
